import React, { useState } from "react";
import { createDM, createChat } from "../../api/api";
import MultiUserSearch from "./MultiUserSearch";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import { Button } from "../ui/button";

/**
 * Modal for starting a new DM or group chat.
 * @param {Object} props
 * @param {number} props.userId - Current user ID
 * @param {function} props.onClose - Callback to close the modal
 * @param {function} props.onChatCreated - Callback when chat is created
 * @returns {JSX.Element}
 */
function NewChatModal({ userId, onClose, onChatCreated }) {
  const [selectionCount, setSelectionCount] = useState(0);
  const minDialogBodyPx = Math.min(440 + Math.min(selectionCount, 16) * 42, 900);
  /**
   * Create a DM or group chat from the selected users list.
   * @param {Array<{ id: number, first_name?: string, last_name?: string, username?: string }>} selectedUsers
   * @returns {Promise<void>}
   */
  const handleCreateChat = async (selectedUsers) => {
    if (selectedUsers.length === 0) return;

    try {
      let res;

      if (selectedUsers.length === 1) {
        res = await createDM(userId, selectedUsers[0].id);
      } else {
        const members = [
          { user_id: userId, role: "admin" },
          ...selectedUsers.map((u) => ({ user_id: u.id, role: "member" })),
        ];

        const chatData = {
          chat_type: "group",
          name: selectedUsers
            .map((u) =>
              u.first_name && u.last_name
                ? `${u.first_name} ${u.last_name}`
                : u.username || "User"
            )
            .join(", "),
          members,
        };

        res = await createChat(chatData);
      }

      if (res.success && res.data) {
        onChatCreated(res.data);
        onClose();
      } else {
        alert(res.error || "Failed to create chat. Please try again.");
      }
    } catch (error) {
      console.error("Create chat error:", error);
      alert("Failed to create chat. Please try again.");
    }
  };

  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <DialogContent
        className="flex max-h-[min(92vh,920px)] min-w-[min(100%,35rem)] flex-col gap-0 overflow-auto p-0 sm:max-w-md resize-y"
        style={{ minHeight: `min(${minDialogBodyPx}px, 88vh)` }}
      >
        <DialogHeader className="shrink-0 space-y-0 border-0 px-6 pb-0 pt-6">
          <div className="flex items-start justify-between gap-3">
            <div>
              <DialogTitle className="text-xl font-semibold tracking-tight">
                New message
              </DialogTitle>
              <DialogDescription className="mt-1.5">
                Search for one person for a direct message, or several for a group chat.
              </DialogDescription>
            </div>
            <DialogClose asChild>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="shrink-0 text-ui-muted-foreground"
                aria-label="Close"
              >
                <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </Button>
            </DialogClose>
          </div>
        </DialogHeader>

        <div className="flex min-h-0 flex-1 flex-col px-6 pb-6 pt-4">
          <div className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-md border border-ui-border bg-ui-background">
            <MultiUserSearch
              userId={userId}
              onSubmit={handleCreateChat}
              submitText="Create chat"
              onSelectionSizeChange={setSelectionCount}
            />
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default NewChatModal;
